import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useSearchParams, Navigate } from 'react-router-dom';
// Eager (first-paint / shell): auth guards, the modal shell, the login pages,
// and the lightweight ErrorPage (rendered inline in several route elements).
import Login from './pages/Login';
import StaffLogin from './pages/StaffLogin';
import ErrorPage from './pages/ErrorPage';
import ProtectedRoute from './components/ProtectedRoute';
import PublicOnlyRoute from './components/PublicOnlyRoute';
import IdentifyMeModal from './components/IdentifyMeModal';
import LoadingState from './components/LoadingState';
import CohortPicker from './components/CohortPicker';
import DemoViewBanner from './components/DemoViewBanner';
import DemoViewOnlyToast from './components/DemoViewOnlyToast';
import { OwnerGuidedTour } from './components/GuidedTour';
import { ActionRequiredProvider } from './hooks/useActionRequired';
import { useAuth } from './contexts/AuthContext';
import { useDemoCohort } from './contexts/DemoCohortContext';

// Route-level code-splitting (UAT-D-009). Each page body is its own Webpack
// chunk, loaded on demand behind the <Suspense> boundary below, so first load
// no longer ships all ~33 pages in the main bundle. The login pages + shell
// stay eager above for fast first paint. All page components are default
// exports, so the plain React.lazy(() => import(...)) form is used throughout.
const Mfa = lazy(() => import('./pages/Mfa'));
const MfaEnrol = lazy(() => import('./pages/MfaEnrol'));
const InviteActivation = lazy(() => import('./pages/InviteActivation'));
const RequestNewInviteLink = lazy(() => import('./pages/RequestNewInviteLink'));
const AccountantActivation = lazy(() => import('./pages/AccountantActivation'));
const Home = lazy(() => import('./pages/Home'));
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'));
const BookingsCalendar = lazy(() => import('./pages/BookingsCalendar'));
const Documents = lazy(() => import('./pages/Documents'));
const Financials = lazy(() => import('./pages/Financials'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const Performance = lazy(() => import('./pages/Performance'));
const Settings = lazy(() => import('./pages/Settings'));
const BankChangeFlagged = lazy(() => import('./pages/BankChangeFlagged'));
const StaffDashboard = lazy(() => import('./pages/StaffDashboard'));
const InternalTools = lazy(() => import('./pages/InternalTools'));
const SyncDiagnostics = lazy(() => import('./pages/SyncDiagnostics'));
const OwnerLookup = lazy(() => import('./pages/OwnerLookup'));
const AuditLogViewer = lazy(() => import('./pages/AuditLogViewer'));
const AdminQueue = lazy(() => import('./pages/AdminQueue'));
const InternalToolsBankChangeQueue = lazy(() => import('./pages/InternalToolsBankChangeQueue'));
const InternalToolsCommunicationConsole = lazy(() => import('./pages/InternalToolsCommunicationConsole'));
const InternalToolsCommunicationAdmin = lazy(() => import('./pages/InternalToolsCommunicationAdmin'));
const InternalToolsCommunicationQueue = lazy(() => import('./pages/InternalToolsCommunicationQueue'));
const InternalToolsImpersonate = lazy(() => import('./pages/InternalToolsImpersonate'));
const InternalToolsInviteOwner = lazy(() => import('./pages/InternalToolsInviteOwner'));
const InternalToolsChangelog = lazy(() => import('./pages/InternalToolsChangelog'));
// Brand Email Status dashboard (Internal Tools PRD Phase 1). Not a tile —
// reached by direct URL (#/brand-email-status) — but as of 2026-08-18 it IS
// behind the portal staff session, plus a per-person allowlist checked by the
// page itself. The static passcode it used to rely on is retired.
const BrandEmailStatusDashboard = lazy(() => import('./pages/BrandEmailStatusDashboard'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
// CSE Portal Guide — internal DEV-ONLY reference (2026-07-22). The page itself
// redirects to the portal on any non-dev host, so it never surfaces in prod.
const CseGuide = lazy(() => import('./pages/CseGuide'));
// WhatsNew page import removed while the What's New tab is hidden for owners
// (Sean 2026-07-02). The /whats-new route redirects to Home below. To restore:
// re-add `const WhatsNew = lazy(() => import('./pages/WhatsNew'));`, restore the
// prefetch entry, and point the route back at <WhatsNew />.

// Core owner-navigation chunks to PREFETCH once the app is idle (2026-06-15).
// react-router v7 navigates via a transition: it keeps the CURRENT page on
// screen until the next route's lazy chunk has downloaded. On a fresh/cold
// property the gallery images are still self-healing (slow WorkDrive round-
// trips) and saturate the network, so that chunk download queues behind them —
// the page appears frozen ("I clicked Financials and nothing happened"), which
// reads as "the portal is broken". Prefetching these chunks during idle means
// the chunk is already in memory when the owner clicks, so navigation is
// instant regardless of image traffic. Webpack dedupes: these specifiers are
// the same chunks the lazy() components above load, so this only warms the
// cache — it never double-bundles. Chunks are small and cached after first
// fetch. Staff-only / public pages are intentionally excluded.
const OWNER_NAV_PREFETCH = [
  () => import('./pages/PropertyDetail'),
  () => import('./pages/BookingsCalendar'),
  () => import('./pages/Documents'),
  () => import('./pages/Financials'),
  () => import('./pages/Maintenance'),
  () => import('./pages/Performance'),
  () => import('./pages/Settings'),
];

// Owner role strings (from staff-role-map.json + Users.role values).
// Anything not in this set is treated as staff and routed to /staff.
// 'stakeholder' (added 2026-05-04) is a read-only owner-side role for
// board / C-suite visibility. See AuthContext.isStakeholderRole.
const OWNER_ROLES = new Set(['owner_contact', 'accountant', 'stakeholder']);

function isStaffRole(role) {
  return !!role && !OWNER_ROLES.has(role);
}

// Role-router for "/" — owners see Home, staff get bounced to /staff.
// Owners get the ActionRequiredProvider so the BellIcon + landing tile
// share a single API fetch per page-load.
function RoleHome() {
  const { user } = useAuth();
  if (isStaffRole(user?.role)) return <Navigate to="/staff" replace />;
  return <ActionRequiredProvider><Home /></ActionRequiredProvider>;
}

// Role-router for "/staff" — staff see the dashboard, owners get bounced home.
function RoleStaff() {
  const { user } = useAuth();
  if (!isStaffRole(user?.role)) return <Navigate to="/" replace />;
  return <StaffDashboard />;
}

// Staff-only route guard — owners get bounced home. Mirrors OwnerOnly.
function StaffOnly({ children }) {
  const { user } = useAuth();
  if (!isStaffRole(user?.role)) return <Navigate to="/" replace />;
  return children;
}

// Owner-only route guard — staff get bounced to /staff.
// Wraps children in ActionRequiredProvider so the BellIcon + tile share
// one API fetch per page-load (Sean's PRD-ASK Q4 answer 2026-05-04).
function OwnerOnly({ children }) {
  const { user } = useAuth();
  if (isStaffRole(user?.role)) return <Navigate to="/staff" replace />;
  return <ActionRequiredProvider>{children}</ActionRequiredProvider>;
}

function App() {
  const { user } = useAuth();
  // Board / Senior-Management demo cohort login (2026-06-16).
  const demo = useDemoCohort();

  // Warm the core owner-navigation chunks once the main thread is idle, so a
  // sidebar click renders instantly even while a cold property's images are
  // still self-healing (see OWNER_NAV_PREFETCH note). Only for a signed-in
  // user — no point prefetching owner pages on the login screen. Idle-scheduled
  // so it never competes with first paint or the active page's own fetches.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      OWNER_NAV_PREFETCH.forEach((load) => { load().catch(() => {}); });
    };
    const ric = typeof window !== 'undefined' ? window.requestIdleCallback : null;
    const handle = ric ? ric(warm, { timeout: 4000 }) : setTimeout(warm, 1500);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(handle);
      else if (!ric) clearTimeout(handle);
    };
  }, [user]);

  // Resolving whether this is the shared demo cohort login (brief; only ever
  // true for a stakeholder / already-switched cohort session). Hold the shell
  // so a demo login never flashes the pinned-owner Home before the picker.
  if (demo.loading) {
    return <LoadingState message="Loading..." />;
  }

  // Mandatory landing for the demo login: pick a cohort owner before the portal.
  if (demo.needsSelection) {
    return (
      <>
        <DemoViewOnlyToast />
        <CohortPicker dismissable={false} />
      </>
    );
  }

  return (
    <>
      <IdentifyMeModal />
      <DemoViewBanner />
      <DemoViewOnlyToast />
      <OwnerGuidedTour />
      <Suspense fallback={<LoadingState message="Loading..." />}>
      <Routes>
      <Route path="/" element={<ProtectedRoute><RoleHome /></ProtectedRoute>} />
      <Route path="/property/:propertyId" element={<ProtectedRoute><OwnerOnly><PropertyDetail /></OwnerOnly></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute><OwnerOnly><BookingsCalendar /></OwnerOnly></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><OwnerOnly><Documents /></OwnerOnly></ProtectedRoute>} />
      <Route path="/financials" element={<ProtectedRoute><OwnerOnly><Financials /></OwnerOnly></ProtectedRoute>} />
      <Route path="/maintenance" element={<ProtectedRoute><OwnerOnly><Maintenance /></OwnerOnly></ProtectedRoute>} />
      <Route path="/performance" element={<ProtectedRoute><OwnerOnly><Performance /></OwnerOnly></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><OwnerOnly><Settings /></OwnerOnly></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute><RoleStaff /></ProtectedRoute>} />
      <Route path="/internal-tools" element={<ProtectedRoute><StaffOnly><InternalTools /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/sync-diagnostics" element={<ProtectedRoute><StaffOnly><SyncDiagnostics /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/owner-lookup" element={<ProtectedRoute><StaffOnly><OwnerLookup /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/audit-log" element={<ProtectedRoute><StaffOnly><AuditLogViewer /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/admin-queue" element={<ProtectedRoute><StaffOnly><AdminQueue /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/bank-change-queue" element={<ProtectedRoute><StaffOnly><InternalToolsBankChangeQueue /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/communication-console" element={<ProtectedRoute><StaffOnly><InternalToolsCommunicationConsole /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/communication-admin" element={<ProtectedRoute><StaffOnly><InternalToolsCommunicationAdmin /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/communication-queue" element={<ProtectedRoute><StaffOnly><InternalToolsCommunicationQueue /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/impersonate-owner" element={<ProtectedRoute><StaffOnly><InternalToolsImpersonate /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/invite-owner" element={<ProtectedRoute><StaffOnly><InternalToolsInviteOwner /></StaffOnly></ProtectedRoute>} />
      <Route path="/internal-tools/changelog" element={<ProtectedRoute><StaffOnly><InternalToolsChangelog /></StaffOnly></ProtectedRoute>} />
      {/* Dev-only CSE reference guide — the page redirects to the portal on any non-dev host. */}
      <Route path="/internal-tools/cse-guide" element={<ProtectedRoute><StaffOnly><CseGuide /></StaffOnly></ProtectedRoute>} />

      <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
      <Route path="/staff-login" element={<PublicOnlyRoute><StaffLogin /></PublicOnlyRoute>} />
      <Route path="/mfa" element={<PublicOnlyRoute><Mfa /></PublicOnlyRoute>} />
      <Route path="/mfa/enrol" element={<PublicOnlyRoute><MfaEnrol /></PublicOnlyRoute>} />
      <Route path="/invite/:token" element={<PublicOnlyRoute><InviteActivation /></PublicOnlyRoute>} />
      <Route path="/invite/request-new/:token" element={<PublicOnlyRoute><RequestNewInviteLink /></PublicOnlyRoute>} />
      <Route path="/accountant/invite/:token" element={<PublicOnlyRoute><AccountantActivation /></PublicOnlyRoute>} />

      {/* Brand Email Status dashboard (Internal Tools PRD Phase 1).
          CHANGED 2026-08-18 — was standalone behind a static passcode, which
          meant anyone with the passcode could open it from any browser. Now
          guarded like every other staff surface: ProtectedRoute (signed in) +
          StaffOnly (a staff role). The page itself then checks the per-person
          allowlist and shows a no-access card to staff who aren't on it. */}
      <Route path="/brand-email-status" element={<ProtectedRoute><StaffOnly><BrandEmailStatusDashboard /></StaffOnly></ProtectedRoute>} />

      <Route path="/help" element={<HelpPage />} />
      {/* Settings & Bank Account PRD Chip C — owner clicks the
          "Flag this change" link in the cooling-off email. Public —
          the JWT in ?token=… is the auth surface. */}
      <Route path="/settings/bank-change/flagged" element={<BankChangeFlagged />} />
      {/* What's New tab hidden for owners (Sean 2026-07-02) — redirect any
          direct /whats-new hit to Home. Restore the <WhatsNew /> element when
          the tab is un-hidden. */}
      <Route path="/whats-new" element={<Navigate to="/" replace />} />

      <Route path="/401" element={
        <ErrorPage
          code="401"
          title="You've been signed out"
          message="Your session has ended. Please sign in again to continue."
          ctaLabel="Sign in"
          ctaHref="#/login"
        />
      } />
      <Route path="/error" element={<CodedErrorPage />} />
      <Route path="/403" element={
        <ErrorPage
          code="403"
          title="You don't have access to this"
          message="If you believe this is a mistake, please contact your property manager."
          ctaLabel="Go home"
          ctaHref="#/"
        />
      } />
      <Route path="*" element={
        <ErrorPage
          code="404"
          title="We couldn't find that page"
          message="The page you're looking for doesn't exist or may have been moved."
          ctaLabel="Go home"
          ctaHref="#/"
        />
      } />
      </Routes>
      </Suspense>
    </>
  );
}

function CodedErrorPage() {
  const [params] = useSearchParams();
  const code = params.get('code');
  // Staff sign-in refused because this login is ALSO a property owner. Users is
  // keyed by email, so letting it through would overwrite the owner's row and
  // strip their access — the refusal is deliberate, and it needs to say so
  // rather than bouncing the person back to a blank login page.
  if (code === 'owner_account_conflict') {
    return (
      <ErrorPage
        code="403"
        title="This email is already an owner login"
        message={
          'This address is set up as an owner login, so it can’t also be used for staff sign-in — ' +
          'signing in as staff would remove that owner’s access to their properties.'
        }
        ctaLabel="Back to sign in"
        ctaHref="#/login"
      />
    );
  }
  if (code === 'no_staff_role') {
    return (
      <ErrorPage
        code="403"
        title="Your account isn't set up for this portal"
        message="Contact IT to have your staff account added, then try signing in again."
        ctaLabel="Back to sign in"
        ctaHref="#/login"
      />
    );
  }
  return (
    <ErrorPage
      code="Error"
      title="Something went wrong"
      message="Please try signing in again. If the problem persists, contact support."
      ctaLabel="Back to sign in"
      ctaHref="#/login"
    />
  );
}

export default App;
